import { api, getAuthToken, ApiRequestError } from '@/lib/api'
import { ApiErrorSchema } from '@/lib/schemas'
import type { ActivityRecord } from '@/components/activity/ActivityRow'

// ===================
// Types
// ===================

export interface ActivityFilters {
  actor?: string
  type?: string
  from?: string
  to?: string
}

interface PaginationOptions {
  limit?: number
  offset?: number
}

const PAGE_SIZE = 50

// ===================
// API Client
// ===================

/**
 * Fetch a page of activity records for a bid.
 */
export async function fetchBidActivity(
  bidId: string,
  filters: ActivityFilters,
  { limit = PAGE_SIZE, offset = 0 }: PaginationOptions = {}
): Promise<{ items: ActivityRecord[]; total: number }> {
  const params: Record<string, string | number | boolean | undefined> = {
    limit,
    offset,
  }
  if (filters.actor) params['actor'] = filters.actor
  if (filters.type) params['type'] = filters.type
  if (filters.from) params['from'] = filters.from
  if (filters.to) params['to'] = filters.to

  return api.get<{ items: ActivityRecord[]; total: number }>(
    `/bids/${bidId}/activity`,
    params
  )
}

/**
 * Export all activity for a bid as a CSV download.
 *
 * Uses a manual authenticated fetch (JWT header) — not the JSON-parsing
 * apiRequest — then triggers a blob download. Modeled on exportArtifactToDocx
 * in api.ts (auth header pattern) and BidsTable.exportToCSV (blob download
 * pattern).
 */
export async function exportBidActivityCsv(
  bidId: string,
  filters: ActivityFilters
): Promise<void> {
  const token = await getAuthToken()

  const url = new URL(
    `${import.meta.env['VITE_API_BASE_URL'] ?? '/api'}/bids/${bidId}/activity`,
    window.location.origin
  )
  url.searchParams.set('format', 'csv')
  if (filters.actor) url.searchParams.set('actor', filters.actor)
  if (filters.type) url.searchParams.set('type', filters.type)
  if (filters.from) url.searchParams.set('from', filters.from)
  if (filters.to) url.searchParams.set('to', filters.to)

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      Accept: 'text/csv',
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const parsed = ApiErrorSchema.safeParse(errorData)
    throw new ApiRequestError(
      parsed.success
        ? parsed.data
        : { code: `HTTP_${response.status}`, message: 'CSV export failed' },
      response.status
    )
  }

  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = `bid-activity-${bidId}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(objectUrl)
}
